/**
 * CRM Hygiene Drafter — worker-reasoned fixes with a MANDATORY
 * deterministic fallback.
 *
 * Every {@link HygieneFinding} already carries a deterministic
 * `proposedFix` from the scanner. This module upgrades that draft with a
 * reasoned fix from the real Claude worker WHEN one is available — and
 * guarantees the pipeline never blocks on it:
 *
 *   - `ctx.runWorker` absent            → fallback (deterministic, instant)
 *   - `runWorker` throws (sync or async) → fallback
 *   - `runWorker` rejects                → fallback
 *   - `runWorker` hangs past the timeout → fallback
 *   - `runWorker` returns a malformed diff → fallback
 *
 * `draftFix` ALWAYS resolves. The `source` field ('worker' | 'fallback')
 * is the honest label the UI surfaces — the operator always knows whether
 * a fix was reasoned by the model or compiled deterministically.
 *
 * PURE seam: the worker is injected via `ctx`, so this module never
 * imports argoWorker, the store, or firebase. Callers (the UI) wire
 * `ctx.runWorker` to src/lib/agent/argoWorker.ts when it is configured.
 */

import type { ActionDiff } from './inlineEditDiff';
import type { HygieneFinding } from './hygieneScanner';

// ===========================================================================
// Types
// ===========================================================================

/** Injected worker seam + tunables. Everything optional by design. */
export type DraftCtx = {
  /** Route a drafting prompt to the real worker. Absent → pure fallback. */
  runWorker?: (prompt: string) => Promise<{ diff: ActionDiff; rationale: string }>;
  /** Max ms to wait for the worker before falling back. Default 15 000. */
  timeoutMs?: number;
};

/** One drafted fix, ready for bundling: the finding + the diff to apply. */
export type DraftedFix = {
  finding: HygieneFinding;
  diff: ActionDiff;
  rationale: string;
  /** Honest origin label — 'worker' only when the real worker answered. */
  source: 'worker' | 'fallback';
};

// ===========================================================================
// Prompt + fallback rationale (both deterministic)
// ===========================================================================

/** The drafting prompt sent to the worker — JSON-safe and self-contained. */
export function draftPrompt(finding: HygieneFinding): string {
  return [
    'You are the ARGO CRM hygiene drafter. Propose ONE reversible field-level fix',
    'for the finding below. Reply with JSON: {"diff":{"recordId":string,',
    '"changes":[{"attr":string,"from":any,"to":any}],"summary":string},"rationale":string}.',
    `Finding: ${JSON.stringify({
      id: finding.id,
      kind: finding.kind,
      recordId: finding.recordId,
      severity: finding.severity,
      evidence: finding.evidence,
      deterministicDraft: finding.proposedFix,
    })}`,
  ].join('\n');
}

/** The deterministic rationale used whenever the worker did not answer. */
export function fallbackRationale(finding: HygieneFinding): string {
  return `Deterministic fix (${finding.kind}): ${finding.evidence.join(' ')}`;
}

// ===========================================================================
// Validation — never trust a worker payload shape
// ===========================================================================

function isValidDiff(d: unknown): d is ActionDiff {
  if (typeof d !== 'object' || d === null) return false;
  const diff = d as Record<string, unknown>;
  if (typeof diff.recordId !== 'string' || diff.recordId.trim() === '') return false;
  if (typeof diff.summary !== 'string' || diff.summary.trim() === '') return false;
  if (!Array.isArray(diff.changes) || diff.changes.length === 0) return false;
  return diff.changes.every(
    (c) =>
      typeof c === 'object' &&
      c !== null &&
      typeof (c as { attr?: unknown }).attr === 'string' &&
      'to' in (c as object),
  );
}

function isValidDraft(v: unknown): v is { diff: ActionDiff; rationale: string } {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as { diff?: unknown; rationale?: unknown };
  return isValidDiff(o.diff) && typeof o.rationale === 'string';
}

/** Race a promise against a timeout; the timer is always cleared. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Worker draft timed out after ${ms}ms`)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
}

// ===========================================================================
// draftFix / draftAll
// ===========================================================================

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Draft the fix for one finding. Uses `ctx.runWorker` when provided and it
 * answers with a valid draft inside the timeout; otherwise falls back to
 * the scanner's deterministic `proposedFix`. ALWAYS resolves — a hygiene
 * run can never be blocked by a flaky or absent worker.
 */
export async function draftFix(
  finding: HygieneFinding,
  ctx: DraftCtx,
): Promise<{ diff: ActionDiff; rationale: string; source: 'worker' | 'fallback' }> {
  const { runWorker } = ctx;
  if (runWorker) {
    try {
      const result = await withTimeout(
        // Wrapped in the try so a SYNCHRONOUS throw from runWorker also
        // routes to the fallback rather than rejecting draftFix.
        Promise.resolve(runWorker(draftPrompt(finding))),
        ctx.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      );
      if (isValidDraft(result)) {
        const rationale = result.rationale.trim();
        return {
          diff: result.diff,
          rationale: rationale !== '' ? rationale : fallbackRationale(finding),
          source: 'worker',
        };
      }
    } catch {
      // Any failure mode (reject/throw/timeout) lands in the fallback below.
    }
  }
  return {
    diff: finding.proposedFix,
    rationale: fallbackRationale(finding),
    source: 'fallback',
  };
}

/**
 * Draft every finding (in parallel — each draft is independent) and return
 * DraftedFixes in the findings' original order. Never rejects: each item
 * individually degrades to its deterministic fallback.
 */
export async function draftAll(
  findings: HygieneFinding[],
  ctx: DraftCtx,
): Promise<DraftedFix[]> {
  const drafted = await Promise.all(findings.map((f) => draftFix(f, ctx)));
  return drafted.map((d, i) => ({ finding: findings[i], ...d }));
}
